// AiAnswerProof — screenshots of ChatGPT and Perplexity recommending clients.
// Used on /client-stories (all of them) and on a case study page (its own).
// Layout is CSS grid with auto-fill, not isMobile branching, so the prerendered
// HTML paints the right layout at any width.

import { checkedLabel, type AiAnswerProof as Proof } from "@/data/aiAnswerProof";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const DARK = { card: "#111111", border: "rgba(255,255,255,0.07)", ink: "white", mid: "rgba(255,255,255,0.62)", soft: "rgba(255,255,255,0.45)", faint: "rgba(255,255,255,0.35)", accent: "#57d7e2", accentBg: "rgba(87,215,226,0.12)", accentBorder: "rgba(87,215,226,0.35)" };
const LIGHT = { card: "#ffffff", border: "#e5e3de", ink: "#111111", mid: "#555555", soft: "#777777", faint: "#999999", accent: "#0fa3b1", accentBg: "rgba(15,163,177,0.07)", accentBorder: "rgba(15,163,177,0.25)" };

export default function AiAnswerProof({ items, minCol = 340, light = false }: { items: Proof[]; minCol?: number; light?: boolean }) {
  if (items.length === 0) return null;
  const t = light ? LIGHT : DARK;
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${minCol}px), 1fr))`, gap: "20px" }}>
      {items.map(p => (
        <figure key={p.id} style={{ margin: 0, background: t.card, border: `1px solid ${t.border}`, borderRadius: "16px", overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <a href={p.image} target="_blank" rel="noopener" style={{ display: "block", background: "#f7f7f8", lineHeight: 0 }} aria-label={`Open the ${p.engine} screenshot full size`}>
            <img
              src={p.image}
              width={p.width}
              height={p.height}
              loading="lazy"
              decoding="async"
              alt={`${p.engine} answer to "${p.prompt}", with the client's name blurred`}
              style={{ width: "100%", height: "220px", objectFit: "cover", objectPosition: "top center", display: "block" }}
            />
          </a>
          <figcaption style={{ padding: "20px 22px 22px", display: "flex", flexDirection: "column", gap: "10px", flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              <span style={{ background: t.accentBg, color: t.accent, border: `1px solid ${t.accentBorder}`, borderRadius: "999px", padding: "3px 10px", fontSize: "12px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.02em" }}>
                {p.position === "Named" ? `Named by ${p.engine}` : `${p.position} on ${p.engine}`}
              </span>
              <span style={{ color: t.soft, fontSize: "13px", fontFamily: ff }}>{p.industry}</span>
            </div>
            <p style={{ color: t.ink, fontSize: "17px", fontWeight: 600, fontFamily: ff, lineHeight: 1.4, margin: 0 }}>"{p.prompt}"</p>
            <p style={{ color: t.mid, fontSize: "15px", fontFamily: ff, lineHeight: 1.6, margin: 0, flex: 1 }}>{p.note}</p>
            <p style={{ color: t.faint, fontSize: "12px", fontFamily: ff, margin: 0 }}>Checked {checkedLabel(p.checked)}. Client name blurred.</p>
          </figcaption>
        </figure>
      ))}
    </div>
  );
}
